// The spec workspace's words and grouping (0051 slice 3): the chip words, the
// rollup order, the drawer's and the column's lines, and the grouping of the
// served spec into layers, areas and rows. The one home of these strings. The
// column and the drawer word what the server decides (planner.SpecViewOf) and
// decide nothing. The two strings that name who builds the product read the
// product's word through productWord (0038). Pure: no React.
import type { components } from "../api/types";
import { capitalized, productWord } from "./productWords";
import { titleNamesCitedDecision } from "./reviewLines";
import { stripAnchors } from "./stripAnchors";

type Node = components["schemas"]["Node"];

export type SpecView = components["schemas"]["SpecView"];
export type SpecRow = components["schemas"]["SpecRow"];
export type SpecLayer = components["schemas"]["SpecLayer"];
export type SpecRowState = SpecRow["state"];
// The product's words as the spec read serves them (the four slots, without
// their system-internal basis); null until a pass names them.
export type SpecWords = SpecView["meta"]["product_words"] | undefined;

// ---- The chip ---------------------------------------------------------

// RULED 2026-10-13: six words over the seven served states. Five are the
// spec's own state words; an answered question reads "Saved", the word
// Review's question card shows for it, so one item never has two words.
export const STATE_WORD: Record<SpecRowState, string> = {
  confirmed: "Confirmed",
  proposed: "Proposed",
  rejected: "Rejected",
  blocked: "Blocker",
  open: "Open gap",
  answered: "Saved",
  saved: "Saved",
};

// The order an area's chip reads its rows' states in (progress-visibility-4:
// blocker, proposed founder action, confirmed, then the rest, a rejected row
// last). An open gap leads a proposal: it needs an answer before anything can
// be proposed. Answered and saved items wait on nothing the founder does.
export const ROLLUP_ORDER: readonly SpecRowState[] = [
  "blocked",
  "open",
  "proposed",
  "confirmed",
  "answered",
  "saved",
  "rejected",
];

// rollupState is the first state of ROLLUP_ORDER among `states`, null for none.
export function rollupState(states: readonly SpecRowState[]): SpecRowState | null {
  for (const state of ROLLUP_ORDER) {
    if (states.includes(state)) return state;
  }
  return null;
}

// ---- The date beside the chip -------------------------------------------

// The RFC3339 date-time the engine writes (Go's time.RFC3339, in UTC). The
// shape is checked before parsing: a bare Date parse reads a loose string
// such as "Sep 4" as a day in 2001.
const RFC3339_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

// dateLabel words the row's served status date (SpecRow.status_set_at, the
// moment the item entered its current status; the spec read decides which
// rows carry one) as the viewer's local day, "Sep 4", with the year only when
// it is not the viewer's current year ("Sep 4, 2025"). "" when there is none
// or it is not the engine's date-time shape: no date then, never another.
export function dateLabel(iso: string | undefined, now: Date = new Date()): string {
  const s = (iso ?? "").trim();
  if (!RFC3339_DATE_TIME.test(s)) return "";
  const day = new Date(s);
  if (Number.isNaN(day.getTime())) return "";
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  if (day.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return day.toLocaleDateString("en-US", opts);
}

// ---- The drawer's lines (RULED 2026-10-13) ------------------------------

// A confirmed decision names each decision it follows from (its entailed-by
// parents); it never says who confirmed it.
export function entailedLine(title: string): string {
  return `Follows from "${title}"`;
}

// A blocked decision names each question that holds it up.
export function heldUpLine(title: string): string {
  return `Held up by "${title}"`;
}

// The heading Review's cards show over the same field (the reasoning).
export const REASONING_HEADING = "Reasoning";

// The heading over a confirmed decision's detail for whoever builds the
// product, in the product's own word (the generic word until a pass names it).
export function builderDetailHeading(words: SpecWords): string {
  return `Details for the ${productWord(words, "who_builds_it")}`;
}

export const DEPENDENTS_HEADING = "What builds on this";
export const EARLIER_VERSIONS_HEADING = "Earlier versions";

// One earlier version's fold, by its title.
export function earlierVersionLine(title: string): string {
  return `"${title}"`;
}

export const REVIEW_IT_ACT = "Review it →";
export const AMEND_ACT = "Amend";
// The amend box's hint, inside the box, and its accessible name.
export const AMEND_PLACEHOLDER = "What should it say instead?";
// The close button's accessible name and tooltip (its visible mark is ✕).
export const CLOSE_LABEL = "Close";

// A pending change's title: the decision it changes, by that decision's title.
export const CHANGE_TITLE_LEAD = "Your change to ";
export function changeTitle(title: string): string {
  return `${CHANGE_TITLE_LEAD}"${title}"`;
}
// A pending change whose decision the read does not hold.
export const CHANGE_TITLE_UNKNOWN = "Your change";

// The lead over the founder's reason on a proposal they turned down.
export const REJECTION_REASON_LABEL = "Your reason";

// 0160 (RULED 2026-10-20): a waiting change's way out. WITHDRAW_CHANGE_ACT is
// the link under the saved line of a decision whose change waits on one of its
// own carriers; WITHDRAWN_LINE is the line that stands in the act's place after
// it, above Amend (the withdraw never touches the decision).
export const WITHDRAW_CHANGE_ACT = "Withdraw this change";
export const WITHDRAWN_LINE = "Withdrawn. The decision stays as it was.";

// ---- The column's lines (RULED 2026-10-13) ------------------------------

export const OUR_READ_LABEL = "Our read of your idea";
export const OUR_READ_NOTE = "You haven't approved this.";
// Our read's sections, in the order the product page prints them, as it
// labels them.
export const OUR_READ_SECTIONS = [
  ["moat", "Moat"],
  ["value_mechanism", "Value mechanism"],
  ["core_experience", "Core experience"],
  ["differentiation", "Differentiation"],
  ["abandonment_triggers", "Abandonment triggers"],
] as const satisfies readonly (readonly [keyof SpecView["meta"], string])[];

// The third filter: the decisions the founder handed to whoever builds the
// product, in the product's own word (the generic word until a pass names it),
// the lead the hand-off option carries.
export function buildersChoice(words: SpecWords): string {
  return `${capitalized(productWord(words, "who_builds_it"))}'s choice`;
}

export type SpecFilter = "all" | "open" | "delegated";

export const FILTER_LABELS = { all: "All", open: "Still open" } as const;

// filterLabel is a filter's word; the third reads the product's word.
export function filterLabel(filter: SpecFilter, words: SpecWords): string {
  return filter === "delegated" ? buildersChoice(words) : FILTER_LABELS[filter];
}

export const WATCHING_HEADING = "What we're watching";
// The accessible name and tooltip of a settled layer head's mark.
export const LAYER_SETTLED_LABEL = "Settled";

// ---- Titles ---------------------------------------------------------------

// changeTarget is the decision a default amend carrier changes: for a question
// whose title names a decision it cites (titleNamesCitedDecision), the first
// cites target the map holds as a decision whose id the title contains as a
// whole token; else undefined. One rule: nodeTitle and the drawer's head read it.
export function changeTarget(node: Node, nodeById: ReadonlyMap<string, Node>): Node | undefined {
  if (node.type !== "question" || !titleNamesCitedDecision(node, nodeById)) return undefined;
  for (const ref of node.refs ?? []) {
    if (ref.rel !== "cites") continue;
    const target = nodeById.get(ref.to);
    if (target === undefined || target.type !== "decision") continue;
    // The allowlist's whole-token rule, asked of this one ref.
    if (titleNamesCitedDecision({ ...node, refs: [ref] }, nodeById)) return target;
  }
  return undefined;
}

// nodeTitle is a node's title as the founder reads it: anchor-stripped and
// trimmed; a default amend carrier's machine title (it names its target's id)
// reads changeTitle(<the target's title>), or CHANGE_TITLE_UNKNOWN when the
// map does not hold the target. Never a raw id.
export function nodeTitle(node: Node, nodeById: ReadonlyMap<string, Node>): string {
  if (node.type === "question" && titleNamesCitedDecision(node, nodeById)) {
    const target = changeTarget(node, nodeById);
    const title = target === undefined ? "" : stripAnchors(target.title ?? "").trim();
    return title !== "" ? changeTitle(title) : CHANGE_TITLE_UNKNOWN;
  }
  return stripAnchors(node.title ?? "").trim();
}

// rowTitle is a row's title (nodeTitle of its node).
export function rowTitle(row: SpecRow, nodeById: ReadonlyMap<string, Node>): string {
  return nodeTitle(row.node, nodeById);
}

// specNodeMap is every node the spec read serves by id: each row's node, then
// each context node not already in it.
export function specNodeMap(spec: SpecView): Map<string, Node> {
  const m = new Map<string, Node>();
  for (const row of spec.rows) {
    if (!m.has(row.node.id)) m.set(row.node.id, row.node);
  }
  for (const n of spec.context) {
    if (!m.has(n.id)) m.set(n.id, n);
  }
  return m;
}

// ---- Filtering and grouping ----------------------------------------------

// rowPasses: `all` keeps every row; `open` the rows not confirmed and not
// rejected; `delegated` the rows the server marks delegated.
export function rowPasses(row: SpecRow, filter: SpecFilter): boolean {
  switch (filter) {
    case "all":
      return true;
    case "open":
      return row.state !== "confirmed" && row.state !== "rejected";
    case "delegated":
      return row.delegated;
  }
}

export type AreaGroupData = { id: string; name: string; rows: SpecRow[] };
export type LayerGroupData = { layer: SpecLayer | null; areas: AreaGroupData[]; loose: SpecRow[] };

// groupSpec groups the rows that pass `filter`: one group per served layer, in
// served order (each kept when none of its rows passes, so a layer's head never
// hides behind a filter), then one group with no layer for the passing rows
// whose layer matches no served layer (only when there are any). Inside a
// group, the rows whose area the served topics name, by area in order of first
// appearance, and the rest loose, in served order.
export function groupSpec(spec: SpecView, filter: SpecFilter): LayerGroupData[] {
  const passing = spec.rows.filter((row) => rowPasses(row, filter));
  const group = (layer: SpecLayer | null, rows: SpecRow[]): LayerGroupData => {
    const areas: AreaGroupData[] = [];
    const loose: SpecRow[] = [];
    for (const row of rows) {
      const topic = row.area.trim() !== "" && Object.hasOwn(spec.meta.topics, row.area)
        ? spec.meta.topics[row.area]
        : undefined;
      if (topic === undefined) {
        loose.push(row);
        continue;
      }
      let area = areas.find((a) => a.id === row.area);
      if (area === undefined) {
        area = { id: row.area, name: topic.display_name, rows: [] };
        areas.push(area);
      }
      area.rows.push(row);
    }
    return { layer, areas, loose };
  };
  const out = spec.layers.map((layer) => group(layer, passing.filter((row) => row.layer === layer.ring)));
  const rings = new Set(spec.layers.map((layer) => layer.ring));
  const unplaced = passing.filter((row) => !rings.has(row.layer));
  if (unplaced.length > 0) out.push(group(null, unplaced));
  return out;
}

// ---- A failed read --------------------------------------------------------

// The pages that read the spec read (the product page, the map) tell a
// missing idea from one that did not load by one rule (0051 slice 2): a read
// that answered nothing, or failed with a 404, found no idea, and the page
// says so with no Try again (a missing idea never loads); any other failure
// reads lib/nextStep's PAGE_LOAD_ERROR_LINE with TRY_AGAIN_ACT.
export const IDEA_NOT_FOUND_LINE = "Idea not found.";
export function specReadNotFound(error: unknown): boolean {
  return !error || (error as { status?: unknown }).status === 404;
}

// 0051 slice 3 (the map): its words and what it draws (0171 Open question 1, option (a): current decisions and open items).

export const MAP_EMPTY_LINE = "Nothing to map yet.";
export const MAP_NARROW_LINE = "Open the map on a wider screen.";
export const MAP_CRUMB = "map";
// The two kinds of link the map draws, in the allowlist's words
// (lib/reviewLines): what a node builds on, and the answer it came from.
export const MAP_EDGE_WORDS = { cites: "Builds on", source: "From your answer" } as const;
// The zoom and fit buttons' names, read aloud and shown on hover.
export const MAP_ZOOM_LABELS = { in: "Zoom in", out: "Zoom out", fit: "Fit to screen" } as const;

export type MapEdge = { from: string; to: string; rel: string };
export type MapData = {
  nodes: Node[];
  edges: MapEdge[];
  layerNames: ReadonlyMap<number, string>;
  topicNames: ReadonlyMap<string, string>;
  stateOf: ReadonlyMap<string, SpecRowState>;
  titleOf: ReadonlyMap<string, string>;
};

// isMapIdea is the map's one test for the idea at its centre: the node whose
// type is idea (the spec read serves the root in its context). mapDataOf
// finds the idea by it, the canvas draws the idea by it, and the map page
// selects nothing on it.
export function isMapIdea(node: Node): boolean {
  return node.type === "idea";
}

// mapDataOf is what the map draws from the spec read: the idea (the context's
// root copy, when served) then every row whose state is not rejected, in
// served order; the links between drawn rows, first "builds on" (each drawn
// row's served dependents, the server's one rule: never rebuilt from cites
// refs here, so a pending change draws none to the decision it changes), then
// "from your answer" (a drawn row's derived-from or extracted-from ref to
// another drawn row, one kind for both rels); never a link to the idea. The
// served layer names (the blank ones left out), the served area names, and
// each drawn row's state and title (rowTitle). An undefined spec draws nothing.
export function mapDataOf(spec: SpecView | undefined): MapData {
  const nodes: Node[] = [];
  const edges: MapEdge[] = [];
  const layerNames = new Map<number, string>();
  const topicNames = new Map<string, string>();
  const stateOf = new Map<string, SpecRowState>();
  const titleOf = new Map<string, string>();
  if (spec === undefined) return { nodes, edges, layerNames, topicNames, stateOf, titleOf };

  const nodeById = specNodeMap(spec);
  const root = spec.context.find(isMapIdea);
  if (root !== undefined) nodes.push(root);
  const drawn = spec.rows.filter((row) => row.state !== "rejected");
  for (const row of drawn) {
    nodes.push(row.node);
    stateOf.set(row.node.id, row.state);
    titleOf.set(row.node.id, rowTitle(row, nodeById));
  }

  // The idea is never a row, so no link reaches it.
  const isDrawnRow = (id: string) => stateOf.has(id);
  for (const row of drawn) {
    for (const dependent of row.dependents) {
      if (isDrawnRow(dependent)) edges.push({ from: dependent, to: row.node.id, rel: "cites" });
    }
  }
  for (const row of drawn) {
    const from = row.node.id;
    const linked = new Set<string>();
    for (const ref of row.node.refs ?? []) {
      if (ref.rel !== "derived-from" && ref.rel !== "extracted-from") continue;
      if (!isDrawnRow(ref.to) || linked.has(ref.to)) continue;
      linked.add(ref.to);
      edges.push({ from, to: ref.to, rel: "source" });
    }
  }

  for (const layer of spec.layers) {
    if (layer.name.trim() !== "") layerNames.set(layer.ring, layer.name);
  }
  for (const [id, topic] of Object.entries(spec.meta.topics)) {
    topicNames.set(id, topic.display_name);
  }
  return { nodes, edges, layerNames, topicNames, stateOf, titleOf };
}
